// Third Party
import * as React from 'react';
import { toInteger } from 'lodash';

// Utils
import { ListingBySlugPartial } from './utils';
import { formatWholeDollarAmount } from '@utils/money';
import { useQuotes } from './quotes-context';
import { QuoteItemizedType } from '@gql';

// UI
// import { ButtonIcon } from '@components/button-icon';
// import { IconNames } from '@components/icon';
import { Typography } from '@components/typography';
import { CustomTooltip, useCustomTooltip } from '@components/custom-tooltip';
import { PrimaryBreakdown } from '@components/listing-quote-breakdown/listing-quote-breakdown';
import { QuotesCompare } from './quotes-compare/quotes-compare';

type Props = { listing?: ListingBySlugPartial };

export const QuoteBreakdownInfo: React.FC<Props> = () => {
  const { toggle: toggleServiceFeeInfo, isVisible: serviceFeeInfoVisible } =
    useCustomTooltip();
  const { toggle: toggleHostFeeInfo, isVisible: hostFeeInfoVisible } =
    useCustomTooltip();
  const serviceFeeInfoRef = React.useRef<HTMLElement>(null);
  const hostFeeInfoRef = React.useRef<HTMLElement>(null);
  const { numberOfNights, datesPresent, quoteData } = useQuotes();

  const getOrder = (type: QuoteItemizedType) => {
    switch (type) {
      case QuoteItemizedType.Base:
        return 1;
      case QuoteItemizedType.Fee:
        return 2;
      case QuoteItemizedType.Other:
        return 3;
      case QuoteItemizedType.Discount:
        return 4;
      default:
        return 5;
    }
  };

  const quoteBreakdownItems =
    quoteData?.bookingQuoteByListingAndDates.__typename == 'BookingQuote'
      ? quoteData.bookingQuoteByListingAndDates.quoteItems
      : [];
  // Sorting the items based on the type order
  const quoteItems = [...quoteBreakdownItems].sort((a, b) => {
    return getOrder(a.type) - getOrder(b.type);
  });

  const categorizedQuoteItems = quoteItems.reduce(
    (acc, item) => {
      if (!acc[item.type]) {
        acc[item.type] = [];
      }
      acc[item.type].push(item);
      return acc;
    },
    {} as Record<QuoteItemizedType, typeof quoteItems>
  );

  const feeCents = categorizedQuoteItems[
    QuoteItemizedType.Fee || QuoteItemizedType.Other
  ]?.reduce((acc, item) => acc + item.costCents, 0);

  return (
    <div
      className="bg-white flex flex-col justify-between w-full h-full rounded-t-xl space-y-2 pt-2 pb-4 shadow-2xl"
      style={{ zIndex: 1000 }}
    >
      {datesPresent &&
        quoteData &&
        quoteData.bookingQuoteByListingAndDates &&
        quoteData.bookingQuoteByListingAndDates.__typename == 'BookingQuote' &&
        quoteData.bookingQuoteByListingAndDates.quoteItems?.length > 0 &&
        quoteData.bookingQuoteByListingAndDates.totalWithoutTaxesCents && (
          <div className="mt-2 w-full text-gray-dark px-4">
            <div className="overflow-scroll max-h-[95px]">
              <div className="relative space-y-[7px]">
                {quoteItems?.length === 0 && (
                  <div className="flex w-full justify-between space-x-2">
                    <Typography
                      component="span"
                      variant="body2"
                      className="font-medium text-xs"
                    >
                      {formatWholeDollarAmount(
                        Math.round(
                          quoteData.bookingQuoteByListingAndDates
                            .totalWithoutTaxesCents / numberOfNights
                        )
                      )}{' '}
                      x {numberOfNights} nights
                    </Typography>

                    <Typography
                      component="span"
                      variant="body2"
                      className="font-medium"
                    >
                      {formatWholeDollarAmount(
                        quoteData.bookingQuoteByListingAndDates
                          .totalWithoutTaxesCents
                      )}
                    </Typography>
                  </div>
                )}

                {categorizedQuoteItems[QuoteItemizedType.Base]?.length > 0 && (
                  <div className="flex w-full justify-between space-x-2">
                    <Typography
                      component="span"
                      variant="body2"
                      className="font-medium text-xs"
                    >
                      {formatWholeDollarAmount(
                        Math.round(
                          categorizedQuoteItems[QuoteItemizedType.Base][0]
                            .costCents / numberOfNights
                        )
                      )}{' '}
                      x {numberOfNights} nights
                    </Typography>
                    <Typography
                      component="span"
                      variant="body2"
                      className="font-medium"
                    >
                      {formatWholeDollarAmount(
                        categorizedQuoteItems[QuoteItemizedType.Base][0]
                          .costCents
                      )}
                    </Typography>
                  </div>
                )}

                {feeCents > 0 && (
                  <div className="flex w-full justify-between space-x-1">
                    <div className="flex flex-col w-full">
                      <div className="rounded-lg hover:cursor-pointer hover:opacity-80">
                        <div
                          className="hover:cursor-pointer hover:opacity-80"
                          ref={
                            hostFeeInfoRef as React.RefObject<HTMLDivElement>
                          }
                        >
                          <Typography
                            component="span"
                            variant="body2"
                            className="font-medium underline text-xs"
                            onClick={toggleHostFeeInfo}
                          >
                            One-time host charges
                          </Typography>
                        </div>
                        <CustomTooltip
                          id="tooltip"
                          onClickAway={() =>
                            hostFeeInfoVisible && toggleHostFeeInfo()
                          }
                          setIsOpen={toggleHostFeeInfo}
                          isOpen={hostFeeInfoVisible}
                          reference={hostFeeInfoRef.current}
                          placement="bottom-start"
                        >
                          <span className="text-black font-medium text-xs">
                            These are one-time charges by the host to cover
                            cleaning costs and other expenses associated with
                            your stay.
                          </span>
                        </CustomTooltip>
                      </div>
                    </div>
                    <div className="flex flex-row space-x-1.5 justify-end pl-2 mt-0.5">
                      <Typography
                        component="span"
                        variant="body2"
                        className="font-medium"
                      >
                        {formatWholeDollarAmount(toInteger(feeCents))}
                      </Typography>
                    </div>
                  </div>
                )}

                {categorizedQuoteItems[QuoteItemizedType.Discount]?.map(
                  (quoteItem, index) => (
                    <div key={index}>
                      <PrimaryBreakdown quoteBreakdownItem={quoteItem} />
                    </div>
                  )
                )}

                <div className="flex w-full justify-between space-x-1">
                  <div className="flex flex-col w-full">
                    <div className="rounded-lg hover:cursor-pointer hover:opacity-80">
                      <div
                        className=""
                        ref={
                          serviceFeeInfoRef as React.RefObject<HTMLDivElement>
                        }
                      >
                        <Typography
                          component="span"
                          variant="body2"
                          className="font-medium flex-nowrap underline text-xs"
                          onClick={toggleServiceFeeInfo}
                        >
                          Savvy service fee
                        </Typography>
                      </div>
                      <CustomTooltip
                        id="tooltip"
                        onClickAway={() =>
                          serviceFeeInfoVisible && toggleServiceFeeInfo()
                        }
                        setIsOpen={toggleServiceFeeInfo}
                        isOpen={serviceFeeInfoVisible}
                        reference={serviceFeeInfoRef.current}
                        placement="bottom-start"
                      >
                        <span className="text-black font-medium text-xs">
                          Savvy does not charge service fees to the traveler or
                          the host.
                        </span>
                      </CustomTooltip>
                    </div>
                  </div>
                  <div className="flex flex-row space-x-1.5 justify-end pl-2 mt-0.5">
                    <Typography variant="body2" className="font-medium">
                      $0
                    </Typography>
                  </div>
                </div>
              </div>

              <div className="hidden sm:block">
                {<QuotesCompare/>}
              </div>
            </div>

            {/* {listing && (
              <div className="flex w-full justify-between space-x-1 border-t border-gray-light pb-4">
                <div className="pt-4 space-y-4">
                  <Typography
                    component="span"
                    variant="subtitle2"
                    className="flex justify-center text-center text-xs"
                  >
                    Questions? Contact the professional host.
                  </Typography>

                  <div className="flex flex-row w-full justify-center">
                    <SocialSection
                      contacts={{
                        // show marketing url if book direct url is present else it is shown above
                        marketingWebsiteUrl: listing.bookDirectWebsiteUrl
                          ? listing.marketingWebsiteUrl ||
                            listing.account.marketingWebsiteUrl
                          : undefined,
                        facebookPageUrl:
                          listing.facebookPageUrl ||
                          listing.account.facebookPageUrl,
                        instagramUrl:
                          listing.instagramUrl || listing.account.instagramUrl,
                        twitterUrl:
                          listing.twitterUrl || listing.account.twitterUrl,
                        publicPhoneNumber:
                          listing.publicPhoneNumber ||
                          listing.account.phoneNumber,
                        publicEmail:
                          listing.publicEmail || listing.account.email,
                        linkedinUrl: listing.account.linkedinUrl,
                        youtubeUrl:
                          listing.youtubeUrl || listing.account.youtubeUrl,
                      }}
                      shouldShowWebsiteUrlAsButton={false}
                    />
                  </div>
                </div>
              </div>
            )} */}
          </div>
        )}
    </div>
  );
};
